/** @file Session action: commit the reviewed plan as one autorig.applyPlan, guarded by its analysed rev. */
import type { AppliedView, SessionCore } from './autorigTypes';

/**
 * Exactly the reviewed plan is sent (no re-analysis), with `ifRev` = the rev it was analysed at, so a commit that
 * landed meanwhile answers STALE_REV and nothing applies; a second Apply after a lost reply is refused the same way.
 * Once the commit is in, the ghost goes and the state is `applied` even if waiting for the confirmed doc then fails.
 * After a close or a newer run only the view update is dropped: their own start already cleared this plan's ghost
 * (a newer run's ghost stays), and the confirmed rev is still awaited (W3-72).
 */
export async function applyReviewed(core: SessionCore): Promise<void> {
  const view = core.store.getState();
  if (view.kind !== 'review' || view.applying || view.reply.plan.commands.length === 0) return;
  const ticket = core.ticket.n;
  core.store.setState({ ...view, applying: true, failure: null }, true);
  let rev: number;
  try {
    rev = (await core.ports.applyPlan(view.reply.plan, view.reply.rev)).rev;
  } catch (thrown) {
    if (ticket === core.ticket.n) core.store.setState({ ...view, applying: false, failure: { thrown } }, true);
    return;
  }
  if (ticket === core.ticket.n) {
    core.ports.showGhost(null);
    const applied: AppliedView = { kind: 'applied', request: view.request, rev, confidence: view.reply.plan.confidence, undoing: false, undone: false, failure: null };
    core.store.setState(applied, true);
  }
  try {
    await core.ports.confirmed(rev);
  } catch (thrown) {
    const now = core.store.getState();
    if (now.kind === 'applied' && now.rev === rev) core.store.setState({ ...now, failure: { thrown } }, true);
  }
}
